import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import { SCUILoadingLinear, SCUIZoomableImage } from '../../../atomic/atoms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { GetS3PresignedDownloadUrls } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import {
    BatchProcessTrackingQuery,
    type BarcodeProductionItem,
} from '../../../services/studio/StudioQuery/BatchProcessTracking/BatchProcessTrackingQuery.services';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
    BatchProcessCompleted: 'info',
    AiOutputApproved: 'success',
    ManualAIProcessRequired: 'error',
    TransferredToPhotoshop: 'default',
};

type ProductionGroup = {
    key: string;
    date: Date;
    items: BarcodeProductionItem[];
};

const dayKey = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/** Üretim günlerine göre (yeniden eskiye) gruplar; grup içi açı sırası korunur. */
const groupByDay = (items: BarcodeProductionItem[]): ProductionGroup[] => {
    const groups = new Map<string, ProductionGroup>();
    items.forEach((item) => {
        const key = dayKey(item.createDate);
        const existing = groups.get(key);
        if (existing) {
            existing.items.push(item);
        } else {
            groups.set(key, { key, date: new Date(item.createDate), items: [item] });
        }
    });
    return Array.from(groups.values()).map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => a.sortOrder - b.sortOrder || a.rawImageId - b.rawImageId),
    }));
};

const ImageBox = ({
    label,
    src,
    previewSrc,
    ai = false,
    emptyLabel = '',
}: {
    label: string;
    src?: string;
    previewSrc?: string;
    ai?: boolean;
    emptyLabel?: string;
}) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            {ai ? <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} /> : null}
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {label}
            </Typography>
        </Stack>
        <Box
            sx={{
                width: '100%',
                height: 220,
                borderRadius: 1,
                border: 1,
                borderColor: ai ? 'primary.main' : 'divider',
                bgcolor: 'grey.100',
                overflow: 'hidden',
            }}
        >
            {src ? (
                <SCUIZoomableImage
                    src={src}
                    previewSrc={previewSrc || src}
                    alt={label}
                    containerSx={{ width: '100%', height: '100%' }}
                    imageSx={{ objectFit: 'contain', width: '100%', height: '100%' }}
                />
            ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {emptyLabel}
                    </Typography>
                </Box>
            )}
        </Box>
    </Box>
);

export const BarcodeProductionHistory = () => {
    const { t, i18n } = useTranslation();
    const { barcode = '' } = useParams<{ barcode: string }>();

    const [items, setItems] = useState<BarcodeProductionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [urls, setUrls] = useState<Record<string, string>>({});

    const isTurkish = i18n.language?.toLowerCase().startsWith('tr');

    const load = useCallback(async () => {
        if (!barcode) {
            return;
        }
        setLoading(true);
        try {
            const history = await BatchProcessTrackingQuery.GetBarcodeProductionHistory(barcode);
            setItems(history.items);

            // Tek batch: thumbnail + orijinal (zoom önizlemesi) anahtarları.
            const keys = new Set<string>();
            history.items.forEach((item) => {
                [item.rawThumbnail, item.rawFilePath, item.llmThumbnail, item.llmProcessedFilePath].forEach((key) => {
                    if (key) {
                        keys.add(key);
                    }
                });
            });
            if (keys.size > 0) {
                const presigned = await GetS3PresignedDownloadUrls(Array.from(keys));
                const next: Record<string, string> = {};
                presigned.items.forEach((entry) => {
                    if (entry.key && entry.url) {
                        next[entry.key] = entry.url;
                    }
                });
                setUrls(next);
            }
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [barcode]);

    useEffect(() => {
        void load();
    }, [load]);

    const groups = useMemo(() => groupByDay(items), [items]);

    return (
        <SCUIContent>
            <SCUIBreadCrumb />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box minWidth={0}>
                    <Typography variant="h5" fontWeight={900}>
                        {t('studio.barcodeHistory.title', { barcode })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('studio.barcodeHistory.subtitle')}
                    </Typography>
                </Box>

                {loading ? <SCUILoadingLinear /> : null}

                {!loading && groups.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('studio.barcodeHistory.empty')}</Typography>
                    </Paper>
                ) : null}

                {groups.map((group) => {
                    const operators = Array.from(new Set(group.items.map((item) => item.createdBy).filter(Boolean)));
                    return (
                        <Paper key={group.key} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    {t('studio.barcodeHistory.productionOf', {
                                        date: group.date.toLocaleDateString(),
                                    })}
                                </Typography>
                                {operators.length > 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('studio.barcodeHistory.operator')}: {operators.join(', ')}
                                    </Typography>
                                ) : null}
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                                    gap: 2,
                                }}
                            >
                                {group.items.map((item) => {
                                    const label = item.photoTypeDisplayName || item.photoTypeCode;
                                    const statusLabel = (isTurkish && item.statusNameTr) || item.status;
                                    return (
                                        <Box
                                            key={item.rawImageId}
                                            sx={{ p: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider' }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {label}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={statusLabel}
                                                    color={STATUS_COLORS[item.status] ?? 'default'}
                                                    variant="outlined"
                                                />
                                            </Stack>
                                            <Stack direction="row" spacing={1.5}>
                                                <ImageBox
                                                    label={t('studio.barcodeHistory.raw')}
                                                    src={urls[item.rawThumbnail] || urls[item.rawFilePath]}
                                                    previewSrc={urls[item.rawFilePath]}
                                                />
                                                <ImageBox
                                                    ai
                                                    label={`${t('studio.barcodeHistory.llm')} - ${label}`}
                                                    src={urls[item.llmThumbnail] || urls[item.llmProcessedFilePath]}
                                                    previewSrc={urls[item.llmProcessedFilePath]}
                                                    emptyLabel={
                                                        item.llmProcessedFilePath
                                                            ? ''
                                                            : t('studio.barcodeHistory.notProducedYet')
                                                    }
                                                />
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </SCUIContent>
    );
};
